import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./policy.css";
import { useLanguage } from "../../i18n/LanguageContext";

const Policy = () => {
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth <= 768;
    return false;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="policy-page">
      <section className="policy-section">
        <Breadcrumbs />
        <motion.h1
          className="policy-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.5 }}
        >
          {tr("Privacy Policy", "Kebijakan Privasi")}
        </motion.h1>
        <p className="policy-intro">
          {tr(
            "This policy describes how Metagent collects, uses, and protects your information when you use our website and services.",
            "Kebijakan ini menjelaskan cara Metagent mengumpulkan, menggunakan, dan melindungi informasi Anda saat Anda menggunakan situs dan layanan kami."
          )}
        </p>

        <div className="policy-content">
          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("Data We Collect", "Data yang Kami Kumpulkan")}
            </h2>
            <p>
              {tr(
                "We may collect information you provide directly, such as:",
                "Kami dapat mengumpulkan informasi yang Anda berikan secara langsung, seperti:"
              )}
            </p>
            <ul>
              <li>
                {tr(
                  "Email address when you subscribe to our newsletter or contact us.",
                  "Alamat email saat Anda berlangganan newsletter atau menghubungi kami."
                )}
              </li>
              <li>
                {tr(
                  "Name and message content when you submit a contact or inquiry form.",
                  "Nama dan isi pesan saat Anda mengirim form kontak atau pertanyaan."
                )}
              </li>
              <li>
                {tr(
                  "Technical data such as IP address, browser type, and pages visited, to improve our site and services.",
                  "Data teknis seperti alamat IP, tipe browser, dan halaman yang dikunjungi, untuk meningkatkan situs dan layanan kami."
                )}
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("How We Use Your Data", "Cara Kami Menggunakan Data Anda")}
            </h2>
            <p>
              {tr(
                "We use the information we collect to: send you updates and newsletters (if you subscribed), respond to your inquiries, improve our website and user experience, and comply with applicable laws. We do not sell your personal data to third parties.",
                "Kami menggunakan informasi yang kami kumpulkan untuk: mengirim update dan newsletter (jika Anda berlangganan), menjawab pertanyaan Anda, meningkatkan situs dan pengalaman pengguna, serta mematuhi hukum yang berlaku. Kami tidak menjual data pribadi Anda ke pihak ketiga."
              )}
            </p>
          </motion.div>

          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("Cookies and Similar Technologies", "Cookie dan Teknologi Serupa")}
            </h2>
            <p>
              {tr(
                "We may use cookies and similar technologies to remember your preferences, understand how you use our site, and improve performance. You can control cookies through your browser settings.",
                "Kami dapat menggunakan cookie dan teknologi serupa untuk mengingat preferensi Anda, memahami cara Anda menggunakan situs kami, dan meningkatkan performa. Anda dapat mengatur cookie melalui pengaturan browser Anda."
              )}
            </p>
          </motion.div>

          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("Data Retention", "Penyimpanan Data")}
            </h2>
            <p>
              {tr(
                "We retain your data only as long as necessary to fulfill the purposes described in this policy or as required by law. You may request deletion of your data by contacting us.",
                "Kami menyimpan data Anda hanya selama diperlukan untuk memenuhi tujuan yang dijelaskan dalam kebijakan ini atau sesuai ketentuan hukum. Anda dapat meminta penghapusan data Anda dengan menghubungi kami."
              )}
            </p>
          </motion.div>

          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("Your Rights", "Hak Anda")}
            </h2>
            <p>
              {tr(
                "Depending on your location, you may have the right to access, correct, or delete your personal data, object to certain processing, or withdraw consent (e.g. unsubscribe from our newsletter). To exercise these rights, please contact us using the details in the footer.",
                "Tergantung lokasi Anda, Anda dapat memiliki hak untuk mengakses, memperbaiki, atau menghapus data pribadi Anda, menolak pemrosesan tertentu, atau menarik persetujuan (mis. berhenti berlangganan newsletter). Untuk menggunakan hak ini, silakan hubungi kami melalui detail di footer."
              )}
            </p>
          </motion.div>

          <motion.div
            className="policy-block"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
          >
            <h2 className="policy-heading">
              {tr("Contact", "Kontak")}
            </h2>
            <p>
              {tr(
                "For any questions about this Privacy Policy or your personal data, please contact us via the contact form on our website or at the email address provided in the footer.",
                "Untuk pertanyaan tentang Kebijakan Privasi ini atau data pribadi Anda, silakan hubungi kami melalui form kontak di situs kami atau ke alamat email yang tercantum di footer."
              )}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Policy;
